import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';

/**
 * Fetches the current authenticated user from Cognito
 * Returns user object, session, tokens, and profile details
 */
async function fetchCurrentUser() {
  try {
    // Get current authenticated user
    const user = await Auth.currentAuthenticatedUser();

    // Get current session to access tokens
    const session = await Auth.currentSession();

    // Extract tokens
    const idToken = session.getIdToken();
    const accessToken = session.getAccessToken();
    const refreshToken = session.getRefreshToken();

    // Extract user attributes (profile details)
    const attributes = user.attributes || {};

    // Extract token payloads for additional info
    const idTokenPayload = idToken.payload || {};
    const accessTokenPayload = accessToken.payload || {};

    return {
      user: {
        username: user.username,
        email: attributes.email || idTokenPayload.email,
        emailVerified:
          attributes.email_verified === 'true' ||
          idTokenPayload.email_verified === true,
        phoneNumber: attributes.phone_number || idTokenPayload.phone_number,
        phoneNumberVerified:
          attributes.phone_number_verified === 'true' ||
          idTokenPayload.phone_number_verified === true,
        name: attributes.name || idTokenPayload.name,
        givenName: attributes.given_name || idTokenPayload.given_name,
        familyName: attributes.family_name || idTokenPayload.family_name,
        picture: attributes.picture || idTokenPayload.picture,
        sub: attributes.sub || idTokenPayload.sub,
        // Include all other attributes
        ...attributes,
      },
      session: {
        isValid: session.isValid(),
        clockDrift: session.getClockDrift(),
      },
      tokens: {
        idToken: {
          jwtToken: idToken.getJwtToken(),
          payload: idTokenPayload,
          expiration: idToken.getExpiration(),
        },
        accessToken: {
          jwtToken: accessToken.getJwtToken(),
          payload: accessTokenPayload,
          expiration: accessToken.getExpiration(),
        },
        refreshToken: {
          token: refreshToken.getToken(),
        },
      },
      // Raw Cognito user object for advanced use cases
      cognitoUser: user,
      // Raw session for advanced use cases
      cognitoSession: session,
    };
  } catch (error) {
    // If user is not authenticated, return null
    // Amplify may throw a plain string (e.g. "The user is not authenticated",
    // "No current user") or an Error object with code/message.
    if (typeof error === 'string') {
      return null;
    }
    if (
      error.code === 'NotAuthorizedException' ||
      error.message?.includes('not authenticated') ||
      error.message?.includes('No current user')
    ) {
      return null;
    }
    throw error;
  }
}

/**
 * Hook to access current authenticated user data
 * Uses React Query to cache and manage the user state
 *
 * @returns {Object} - { user, session, tokens, isLoading, error, refetch }
 */
export function useAuth() {
  const queryClient = useQueryClient();

  const {
    data: authData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['auth', 'currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 0, // 5 minutes - token
    // gcTime: 1000 * 60 * 1, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: true, // Refetch on reconnect
    refetchInterval: 1000, // 1 second
    refetchIntervalInBackground: true,
  });

  // Helper function to invalidate and refetch user data
  const refreshUser = async () => {
    await queryClient.invalidateQueries({ queryKey: ['auth', 'currentUser'] });
    return refetch();
  };

  // Helper function to clear user data (on logout)
  const clearUser = () => {
    queryClient.setQueryData(['auth', 'currentUser'], null);
  };

  return {
    // User data
    user: authData?.user || null,
    session: authData?.session || null,
    tokens: authData?.tokens || null,
    cognitoUser: authData?.cognitoUser || null,
    cognitoSession: authData?.cognitoSession || null,

    // State
    isLoading,
    error,
    isAuthenticated: !!authData,

    // Actions
    refetch,
    refreshUser,
    clearUser,
  };
}

/**
 * Hook to get just the user object (shorthand)
 */
export function useUser() {
  const { user, isLoading, error, isAuthenticated } = useAuth();
  return { user, isLoading, error, isAuthenticated };
}

/**
 * Hook to get just the tokens (shorthand)
 */
export function useTokens() {
  const { tokens, isLoading, error, isAuthenticated } = useAuth();
  return { tokens, isLoading, error, isAuthenticated };
}
